/**
 * The people chips under the name box in "Send this to someone else"
 * (proposal 7, kanban 0da0c669). They filter on what is typed, with a count
 * ("1 match for “Tim” · clear to see everyone"). The chips are reached with
 * the arrow keys, not Tab: Tab from the name box goes on to the note, and
 * ArrowDown from the box lands on the first chip (roving focus; every chip
 * has tabindex -1, so dozens of chips never sit in the Tab order).
 */
import { useEffect, type KeyboardEvent, type RefObject } from 'react';

import type { DoorRouteTargetRead } from '../../services/qcpInvestigations';
import { trackEvent } from '../../services/portalTracking';
import { filterQuery, filterStaff, matchCountLabel } from './routePickView';
import styles from './RoutePicker.module.css';

/** How long the typing rests before the filter is recorded once. */
const FILTER_TRACK_MS = 500;
const MAX_CHIPS = 24;

export function RoutePeoplePicks({
  staff,
  typed,
  picked,
  onPick,
  onClear,
  onLeaveUp,
  disabled,
  threadNo,
  groupRef,
}: {
  staff: DoorRouteTargetRead[];
  typed: string;
  /** Names the box resolves to now: those chips show as chosen. */
  picked: ReadonlySet<string>;
  onPick: (name: string) => void;
  onClear: () => void;
  /** ArrowUp / ArrowLeft past the first chip goes back to the name box. */
  onLeaveUp: () => void;
  disabled: boolean;
  threadNo: string;
  groupRef: RefObject<HTMLDivElement | null>;
}) {
  const query = filterQuery(typed);
  const matches = filterStaff(staff, typed);
  const shown = matches.slice(0, MAX_CHIPS);
  const count = matches.length;

  useEffect(() => {
    if (!query) return;
    const t = window.setTimeout(() => {
      trackEvent('action', 'qcp_route_filter', {
        thread_no: threadNo,
        query,
        matches: count,
      });
    }, FILTER_TRACK_MS);
    return () => window.clearTimeout(t);
  }, [query, count, threadNo]);

  if (staff.length === 0) return null;

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const chips = Array.from(
      groupRef.current?.querySelectorAll<HTMLButtonElement>('button[data-chip]') ?? [],
    );
    const at = chips.findIndex((c) => c === document.activeElement);
    if (at < 0) return;
    let next: number | null = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = Math.min(at + 1, chips.length - 1);
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = at - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = chips.length - 1;
    if (next === null) return;
    e.preventDefault();
    if (next < 0) onLeaveUp();
    else chips[next].focus();
  };

  return (
    <>
      {query && (
        <div className={styles.matchLine} data-testid="route-match-count">
          {matchCountLabel(matches.length, query)}
          {' · '}
          <button
            type="button"
            className={styles.clearBtn}
            tabIndex={-1}
            onClick={() => {
              onClear();
              trackEvent('action', 'qcp_route_filter_clear', { thread_no: threadNo, query });
            }}
          >
            clear
          </button>{' '}
          to see everyone
        </div>
      )}
      {shown.length > 0 && (
        <div
          ref={groupRef}
          className={styles.peoplePicks}
          role="group"
          aria-label="People you can send this to"
          onKeyDown={onKeyDown}
        >
          {shown.map((t) => {
            const on = picked.has(t.name);
            return (
              <button
                key={`${t.name}-${t.email ?? ''}`}
                type="button"
                data-chip
                tabIndex={-1}
                className={styles.personChip}
                data-on={on || undefined}
                aria-pressed={on}
                disabled={disabled}
                data-action="qcp_route_chip_pick"
                data-target-thread-no={threadNo}
                onClick={() => {
                  onPick(t.name);
                  trackEvent('action', 'qcp_route_chip_pick', { thread_no: threadNo, name: t.name, query });
                }}
              >
                {on && <span aria-hidden="true">✓ </span>}
                {t.name}
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}

export default RoutePeoplePicks;
